import React, { useEffect, useState } from "react";
import {
  Button,
  Divider,
  Form,
  Input,
  Modal,
  Select,
  Typography,
  Space,
} from "antd";
import {
  OAuthClient,
  OAuthClientWithoutClientSecret,
} from "../../../../Entities/OAuthClient";
import { openNotification } from "../../../../Components/Notification";
import { useLoader } from "../../../../Context/LoaderContext";
import { CopyOutlined, InfoCircleOutlined } from "@ant-design/icons";
import oAuthClientService from "../../../../Service/o.auth.client.service";

interface CreateOAuthClientModalProps {
  showCreateNewClientModal: boolean;
  setShowCreateNewClientModal: (show: boolean) => void;
  setOAuthClients: React.Dispatch<
    React.SetStateAction<OAuthClientWithoutClientSecret[]>
  >;
  selectedClient: OAuthClientWithoutClientSecret | null;
  setSelectedClient: React.Dispatch<
    React.SetStateAction<OAuthClientWithoutClientSecret | null>
  >;
}

const scopeOptions = [
  {
    value: "read:jobs",
    label: "Read Job Posts",
  },
  {
    value: "read:applications",
    label: "Read Job Applications",
  },
  {
    value: "read:candidates",
    label: "Read Candidates",
  },
];

const CreateOAuthClientModal: React.FC<CreateOAuthClientModalProps> = ({
  showCreateNewClientModal,
  setShowCreateNewClientModal,
  setOAuthClients,
  selectedClient,
  setSelectedClient,
}) => {
  const [createdClient, setCreatedClient] = useState<OAuthClient | null>(null);
  const { isLoading, setLoading, setLoadingMessage } = useLoader();

  const [form] = Form.useForm();

  const validateHttpsUrl = (_: any, value: string[]) => {
    if (!value || value.length === 0) {
      return Promise.resolve();
    }

    const httpsUrlPattern = /^https:\/\/.+/;
    const invalidUrls = value.filter((url) => !httpsUrlPattern.test(url));

    if (invalidUrls.length > 0) {
      return Promise.reject(
        new Error("All redirect URIs must be valid HTTPS URLs")
      );
    }

    for (const url of value) {
      try {
        new URL(url);
      } catch {
        return Promise.reject(new Error(`"${url}" is not a valid URL`));
      }
    }

    return Promise.resolve();
  };

  const handleCopyToClipboard = async (secret: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(secret);
        openNotification(
          "success",
          "Copied",
          "Client Secret copied to clipboard"
        );
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = secret;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand("copy");
          if (successful) {
            openNotification(
              "success",
              "Copied",
              "Client Secret copied to clipboard"
            );
          } else {
            throw new Error("Copy command failed");
          }
        } catch (err) {
          throw new Error("Copy command failed");
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      openNotification(
        "error",
        "Failed to copy Client Secret",
        "Please copy the key manually from the input field"
      );
    }
  };

  const handleDownloadClientSecret = (secret: string, id: string) => {
    try {
      const fileName = `client_secret_${id}.txt`;
      const blob = new Blob([secret], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
      openNotification("success", "Client Secret Downloaded", "");
    } catch (error) {
      console.error("Failed to download client secret:", error);
      openNotification("error", "Failed to download Client Secret", "");
    }
  };

  const handleCancel = () => {
    setSelectedClient(null);
    setCreatedClient(null);
    form.resetFields();
    setShowCreateNewClientModal(false);
  };

  const handleCreateNewClient = async (values: {
    redirectUris: string[];
    scopes: string[];
  }) => {
    try {
      setLoading(true);
      setLoadingMessage("Creating OAuth client...");

      const newClient = await oAuthClientService.createOAuthClient({
        redirectUris: values.redirectUris,
        scopes: values.scopes,
      });
      if (newClient) {
        const clientWithoutSecret = {
          _id: newClient._id,
          clientId: newClient.clientId,
          orgId: newClient.orgId,
          redirectUris: newClient.redirectUris,
          scopes: newClient.scopes,
          status: newClient.status,
          createdAt: newClient.createdAt,
          updatedAt: newClient.updatedAt,
        };
        setOAuthClients((prev) =>
          [...prev, clientWithoutSecret].sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
        );
        setCreatedClient(newClient);
        handleDownloadClientSecret(newClient.clientSecret, newClient.clientId);
      }
    } catch (error: any) {
      console.error("Error creating client:", error);
      openNotification("error", "Error", error.message);
      setCreatedClient(null);
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleUpdateClient = async (values: {
    redirectUris: string[];
    scopes: string[];
  }) => {
    if (!selectedClient) {
      openNotification("error", "Error", "No client selected");
      return;
    }
    try {
      setLoading(true);
      setLoadingMessage("Updating OAuth client...");
      const updatedClient = await oAuthClientService.updateOAuthClient(
        selectedClient?._id || "",
        {
          redirectUris: values.redirectUris,
          scopes: values.scopes,
          status: selectedClient.status,
        }
      );
      if (updatedClient) {
        const clientWithoutSecret = {
          _id: updatedClient._id,
          clientId: updatedClient.clientId,
          orgId: updatedClient.orgId,
          redirectUris: updatedClient.redirectUris,
          scopes: updatedClient.scopes,
          status: updatedClient.status,
          createdAt: updatedClient.createdAt,
          updatedAt: updatedClient.updatedAt,
        };
        setOAuthClients((prev) =>
          [
            ...prev.map((client) =>
              client._id === updatedClient._id ? clientWithoutSecret : client
            ),
          ].sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
        );
        setSelectedClient(null);
        form.resetFields();
        setShowCreateNewClientModal(false);
        openNotification("success", "Success", "Client updated successfully");
      }
    } catch (error: any) {
      console.error("Error updating client:", error);
      openNotification("error", "Error", error.message);
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleSubmit = async (values: {
    redirectUris: string[];
    scopes: string[];
  }) => {
    if (selectedClient) {
      await handleUpdateClient(values);
      return;
    } else {
      await handleCreateNewClient(values);
      return;
    }
  };

  useEffect(() => {
    if (showCreateNewClientModal) {
      if (selectedClient) {
        form.setFieldsValue({
          redirectUris: selectedClient?.redirectUris || [],
          scopes: selectedClient?.scopes || [],
        });
      } else {
        form.resetFields();
      }
    }
  }, [selectedClient, showCreateNewClientModal, form]);

  return (
    <Modal
      title={selectedClient ? "Update Client" : "Create New Client"}
      open={showCreateNewClientModal}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
      centered
      maskClosable={createdClient ? false : true}
    >
      <section className="create-oauth-client-modal-container">
        {createdClient && (
          <div className="create-oauth-client-modal-client-preview">
            <div className="create-oauth-client-modal-client-preview-info">
              <InfoCircleOutlined />
            </div>

            <Typography.Text>
              Your OAuth client is ready. Please copy and save the client secret
              now — you won’t be able to see it again. Didn’t download it?{" "}
              <Typography.Link
                onClick={() =>
                  handleDownloadClientSecret(
                    createdClient.clientSecret,
                    createdClient.clientId
                  )
                }
              >
                Click here
              </Typography.Link>
            </Typography.Text>

            <Space.Compact style={{ width: "100%" }}>
              <Input
                defaultValue={createdClient?.clientSecret || ""}
                readOnly
              />
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={() =>
                  handleCopyToClipboard(createdClient?.clientSecret || "")
                }
              >
                Copy
              </Button>
            </Space.Compact>

            <Divider style={{ margin: "0" }} />

            <div className="create-oauth-client-modal-client-preview-close-button">
              <Button onClick={handleCancel}>Close</Button>
            </div>
          </div>
        )}

        {!createdClient && (
          <Form
            form={form}
            layout="vertical"
            className="create-oauth-client-modal-form"
            onFinish={handleSubmit}
          >
            <Form.Item
              label="Redirect URIs"
              name="redirectUris"
              rules={[
                {
                  required: true,
                  message: "Please add at least one redirect URI",
                },
                { validator: validateHttpsUrl },
              ]}
              style={{ width: "100%", margin: 0 }}
            >
              <Select
                mode="tags"
                placeholder="Enter HTTPS URLs (e.g., https://example.com)"
                tokenSeparators={[","]}
                style={{ width: "100%" }}
                open={false}
                onDropdownVisibleChange={() => {}}
                dropdownStyle={{ display: "none" }}
              />
            </Form.Item>

            <Form.Item
              label="Scopes"
              name="scopes"
              rules={[
                { required: true, message: "Please select at least one scope" },
              ]}
              style={{ width: "100%", margin: 0 }}
            >
              <Select
                mode="multiple"
                placeholder="Select scopes"
                options={scopeOptions}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Form>
        )}

        {!createdClient && <Divider style={{ margin: "0" }} />}

        {!createdClient && (
          <div className="create-oauth-client-modal-form-footer">
            <Button onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={() => form.submit()}
              loading={isLoading}
            >
              {selectedClient ? "Update" : "Save"}
            </Button>
          </div>
        )}
      </section>
    </Modal>
  );
};

export default CreateOAuthClientModal;
